import React, { useEffect, useState } from "react";
import CustomDataTable from "components/customs/table";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { showDeleteModal, Toast } from "utility/helpers";
import _ from "lodash";
import {
  fetchTable,
  fetchAllAgencies,
  updateRow,
  editTotalBudget,
} from "services/services.projects";
import { genericGet } from "services/services.general";
import { defaultTableOption, rowPerPage } from "constants/table";
import DropdownSelect from "components/customs/dropdownSelect";
import EndorsementsAndBudget from "components/customs/endorsementsAndBudget";
import styled from "styled-components";
import useAuth from "hooks/useAuth";
import LoadingScreen from "components/customs/loading-screen";
import { defaultColumns, newColumns } from "./columns";
import { getAgencySelectorOptions, sortAgency } from "../utility";
import { NO_AFFILIATED_AGENCIES_ACCOUNTS } from "../constants";

const TableSection = styled.div`
  margin-top: 16px;
`;

const FilterSection = styled.div`
  margin-bottom: 16px;
  display: grid;
  grid-template-columns: 500px 250px;
  justify-content: space-between;
  align-items: center;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const Main = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectorOptions, setSelectorOptions] = useState([]);
  const [editingRowId, setEditingRowId] = useState("");
  const [initialProjects, setInitialProjects] = useState([]);
  const [columns, setColumns] = useState([]);
  const [agencyList, setAgencyList] = useState([]);
  const [totalBudget, setTotalBudget] = useState("");
  const [projects, setProjects] = useState([]);
  const [remainingBalance, setRemainingBalance] = useState("");
  const [totalAmountSpent, setTotalAmountSpent] = useState("");
  const [newTotalBudget, setNewTotalBudget] = useState();

  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.type;

  const { id } = useParams();

  const dateRange = useSelector((state) => state.dateRange);
  const { dateFrom, dateTo } = dateRange || {};

  let changeSearchTimeoutID;

  // eslint-disable-next-line no-unused-vars
  const onChangeSearch = (event) => {
    event.persist();

    if (changeSearchTimeoutID) {
      clearTimeout(changeSearchTimeoutID);
    }

    changeSearchTimeoutID = setTimeout(() => {
      const eventValue = event.target.value;

      setTableOption({
        ...tableOption,
        page: 1,
        searchValue: eventValue.trim(),
      });
    }, 1000);
  };

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (column, sortDirection) => {
    if (selectedProject?.ID === 0) {
      const newData = sortAgency(column, sortDirection, tableOption);

      setTableOption({
        ...tableOption,
        data: newData,
      });

      return;
    }

    setTableOption({
      ...tableOption,
      sort: {
        field: column.identifier,
        direction: sortDirection,
      },
    });
  };

  const fetchAgencies = () => {
    fetchAllAgencies({
      callback: (data) => {
        setAgencyList(data);
        const options = getAgencySelectorOptions(data);
        setSelectorOptions(options);

        options?.forEach((op) => {
          if (op?.ID === Number(id)) {
            setSelectedProject(op);
          }
        });
      },
    });
  };

  const fetchData = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    fetchTable({
      url: `/agency/${selectedProject?.ID}/`,
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
        startDate: dateFrom || "",
        endDate: dateTo || "",
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          data: data?.Projects,
          isLoading: false,
          totalRows: data?.projectCount,
        });

        setInitialProjects(data?.Projects);
      },
    });
  };

  const handleFailedRowEdit = () => {
    setTableOption({
      ...tableOption,
      data: initialProjects,
    });
  };

  const fetchBudgetAllocation = () => {
    if (selectedProject?.ID !== 0) {
      genericGet({
        url: `/agency/${selectedProject?.ID}/allocatedBudget`,
        params: {
          startDate: dateFrom || "",
          endDate: dateTo || "",
        },
        callback: (data) => {
          const agencyProjs = data?.projects;

          const totalAgencyBudget =
            data?.totalBudget !== null ? data.totalBudget : 0;

          const amountSpent =
            data?.allocatedBudget !== null ? data?.allocatedBudget : 0;

          const remainingBudget =
            data?.remainingBalance !== null ? data?.remainingBalance : 0;
          let projs = [];

          setTotalBudget(totalAgencyBudget);
          setTotalAmountSpent(amountSpent);
          setRemainingBalance(remainingBudget);

          if (agencyProjs?.length !== 0) {
            projs = agencyProjs?.map((proj) => {
              return {
                id: proj?.id,
                name: proj?.name,
                value: proj?.allocatedBudget,
                budget:
                  proj.allocatedBudget !== 0
                    ? (proj.allocatedBudget / totalAgencyBudget) * 100
                    : 0,
              };
            });
          }

          projs.push({
            id: 0,
            name: "Remaining Balance",
            value: remainingBudget,
            budget:
              remainingBudget !== 0
                ? (remainingBudget / totalAgencyBudget) * 100
                : 0,
          });

          setProjects(projs);
        },
      });
    }
  };

  const updateProjectDetails = (formData, projectId) => {
    updateRow({
      url: `/project/${projectId}`,
      formData,
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated row",
            });
            fetchData();
            fetchBudgetAllocation();
          }, 1500);
        } else {
          handleFailedRowEdit();
        }
      },
    });
  };

  const handleOverallBudgetChange = (budget) => {
    editTotalBudget({
      formData: {
        totalBudget: budget,
      },
      url: `/agency/${id}`,
      callback: (response) => {
        const status = response?.status;

        if (status) {
          Toast({
            type: 1,
            content: "Successfully updated total budget",
          });
          fetchBudgetAllocation();
        } else {
          setNewTotalBudget(totalBudget);
        }
      },
    });
  };

  const hasProjectAccess = () => {
    if (!NO_AFFILIATED_AGENCIES_ACCOUNTS.includes(role)) {
      const agencyIds = user?.agencyIds;

      if (agencyIds && !agencyIds?.includes(Number(id))) {
        navigate("/404");
      }
    }
  };

  const handleDeleteRow = (row) => {
    showDeleteModal({
      title: row.name,
      url: `project/${row.id}`,
      callBack: () => {
        fetchData();
      },
    });
  };

  const handleSaveClicked = (row) => {
    setEditingRowId("");
    const projectId = row?.id;
    const idx = initialProjects.findIndex((item) => item.id === row.id);
    const initialData = initialProjects[idx];
    const newData = {};

    if (!_.isEqual(row, initialData)) {
      // eslint-disable-next-line array-callback-return
      Object.keys(initialData)?.map((obj) => {
        if (initialData[obj] !== row[obj]) {
          newData[obj] = row[obj];
        }
      });
      updateProjectDetails(newData, projectId);
    }
  };

  const handleCancelClicked = (index) => {
    setEditingRowId("");
    const idx = tableOption.data.findIndex((item) => item.id === index);
    const newData = [...tableOption.data];
    newData[idx] = initialProjects[idx];

    setTableOption((prev) => ({
      ...prev,
      data: newData,
    }));
  };

  const NEW_COLUMNS = newColumns({
    editingRowId,
    setEditingRowId,
    tableOption,
    setTableOption,
    selectedProject,
    handleDeleteRow,
    handleSaveClicked,
    handleCancelClicked,
    navigate,
    role,
  });

  useEffect(() => {
    if (selectedProject !== null) {
      if (selectedProject?.value !== null) {
        fetchBudgetAllocation();
      }
    }
  }, [selectedProject]);

  useEffect(() => {
    fetchAgencies();
    hasProjectAccess();
  }, []);

  useEffect(() => {
    if (selectedProject !== null) {
      if (selectedProject?.ID === 0) {
        setTableOption({
          ...tableOption,
          totalRows: agencyList?.length,
          data: agencyList,
          isLoading: false,
        });
        setColumns(defaultColumns);
      } else {
        fetchData();
        setColumns(NEW_COLUMNS);
      }
    }
  }, [
    selectedProject,
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.field,
    tableOption.sort.direction,
  ]);

  useEffect(() => {
    setColumns(NEW_COLUMNS);
  }, [editingRowId]);

  useEffect(() => {
    if (selectedProject) {
      fetchData();
      fetchBudgetAllocation();
    }
  }, [dateFrom]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <FilterSection>
            {/* agency select dropdown here */}
            <DropdownSelect
              isLoading={tableOption.isLoading}
              isDisabled={tableOption.isLoading}
              onChange={(option) => {
                setSelectedProject(option);
                navigate(`/projects/${option?.ID}`);
              }}
              value={selectedProject}
              options={selectorOptions}
              filterOption={(option, searchValue) =>
                option?.label
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase()) ||
                option?.data?.acronym
                  ?.toLowerCase()
                  ?.includes(searchValue?.toLowerCase())
              }
            />
          </FilterSection>

          {selectedProject?.ID !== 0 && (
            <EndorsementsAndBudget
              newTotalBudget={newTotalBudget}
              totalBudget={totalBudget}
              totalAmountSpent={totalAmountSpent}
              projects={projects}
              remainingBalance={remainingBalance}
              handleOverallBudgetChange={handleOverallBudgetChange}
            />
          )}

          <TableSection>
            {/* table here */}
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onSort={onSort}
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationServer={selectedProject?.ID !== 0}
              paginationTotalRows={tableOption.totalRows}
              progressPending={tableOption.isLoading}
              sortServer
            />
          </TableSection>
        </CardContainer>
      </CardSection>
    </Wrapper>
  );
};

export default Main;
